<script lang="ts" setup>
import type { InputDateProps } from '@nuxt/ui';
import type { DateRange, DateValue } from 'reka-ui';
import type { Temporal } from 'temporal-polyfill';
import type { ButtonProps } from '#layers/ui/components/Button.vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatPlainDate } from '#layers/base';
import DButton from '#layers/ui/components/Button.vue';
import DFormField from '#layers/ui/components/Form/FormField.vue';
import DInput from '#layers/ui/components/Input.vue';

interface Props extends Pick<ButtonProps, 'size' | 'disabled'>, Pick<InputDateProps, 'variant'> {
	readonly?: boolean;
	panel: 'start' | 'end';
	selectedDate?: Temporal.PlainDate | null;
	displayRange: DateRange;
	allowNonContiguousRanges: boolean;
	isDateUnavailable: (date: DateValue) => boolean;
	isSetToNowDisabled: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	readonly: false,
});
const emit = defineEmits<{
	'clear': [];
	'setToNow': [];
	'update:calendar': [value: DateRange | null];
}>();

const placeholder = defineModel<DateValue>('placeholder', { required: true });
const calendarUi = {
	gridBody: 'gap-y-0.5',
	gridRow: 'place-items-stretch',
	headCell: 'text-dimmed font-normal',
	heading: 'text-md font-medium',
	cell: '[&:has([data-selected])]:bg-primary/15 [&:has([data-selection-start])]:rounded-s-md [&:has([data-selection-end])]:rounded-e-md',
	cellTrigger: 'm-0 rounded-md data-[selected]:bg-transparent data-[selected]:text-highlighted data-[selected]:data-[selection-start]:bg-primary data-[selected]:data-[selection-start]:text-inverted data-[selected]:data-[selection-start]:rounded-md data-[selected]:data-[selection-end]:bg-primary data-[selected]:data-[selection-end]:text-inverted data-[selected]:data-[selection-end]:rounded-md hover:bg-transparent data-[highlighted]:bg-transparent hover:border-2 hover:border-primary hover:data-[selection-start]:border-0 hover:data-[selection-end]:border-0 data-[unavailable]:opacity-35 data-[unavailable]:cursor-not-allowed data-[unavailable]:pointer-events-auto data-[unavailable]:no-underline data-[unavailable]:hover:bg-transparent data-[unavailable]:hover:border-0',
};
const { t, locale } = useI18n();

const label = computed(() =>
	props.panel === 'start' ? t('date_range_picker.start_date') : t('date_range_picker.end_date'),
);

const fixedDate = computed(() => (props.panel === 'start' ? 'end' : 'start'));

const panelTestId = computed(() => `date-range-${props.panel}-panel`);
const clearTestId = computed(() => `date-range-clear-${props.panel}`);

function onCalendarUpdate(value: DateRange | null) {
	emit('update:calendar', value);
}

function onClear() {
	emit('clear');
}

function onSetToNow() {
	emit('setToNow');
}
</script>

<template>
	<div :data-test-id="panelTestId" class="flex w-fit flex-col gap-2">
		<DFormField :label>
			<DInput
				:model-value="formatPlainDate(selectedDate ?? null, locale)"
				color="neutral"
				:variant
				:size
				:disabled
				:placeholder="$t('date_range_picker.select_a_date')"
				leading-icon="lucide:calendar"
				readonly
				:aria-readonly="readonly"
				class="w-full"
				:ui="{ base: 'ps-10', trailing: 'pe-1' }"
			>
				<template v-if="selectedDate != null" #trailing>
					<DButton
						type="button"
						color="neutral"
						variant="link"
						size="sm"
						icon="lucide:x"
						:data-test-id="clearTestId"
						:aria-readonly="readonly"
						:aria-label="$t('common.clear')"
						:disabled
						@click.stop.prevent="onClear"
					/>
				</template>
			</DInput>
		</DFormField>
		<div class="w-fit overflow-hidden rounded-md border border-default">
			<div class="p-3">
				<DCalendar
					v-model:placeholder="placeholder"
					:model-value="displayRange"
					range
					:fixed-date
					:year-controls="false"
					:allow-non-contiguous-ranges
					:is-date-unavailable
					:ui="calendarUi"
					@update:model-value="onCalendarUpdate"
				/>
			</div>
			<template v-if="!readonly">
				<DSeparator />
				<DButton
					color="neutral"
					variant="ghost"
					class="w-full justify-center rounded-t-none py-3"
					data-test-id="date-range-set-to-now"
					:disabled="disabled || isSetToNowDisabled"
					@click="onSetToNow"
				>
					{{ $t('date_range_picker.set_to_now') }}
				</DButton>
			</template>
		</div>
	</div>
</template>
